/**
 * The persistent shell — the reason this app is React at all.
 *
 * The rail and top bar mount ONCE. Navigation swaps only the <Outlet/>;
 * nothing else repaints, which is the no-flash behaviour the static portal
 * could only approximate. Markup and classes are the existing design system's
 * (site/portal/assets/app.css) so the two shells stay pixel-identical during
 * the migration.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from './api';
import { RequireSession, useSession } from './session';

function Decagon() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <polygon
        points="16.00,2.50 23.94,5.08 28.84,11.83 28.84,20.17 23.94,26.92 16.00,29.50 8.06,26.92 3.16,20.17 3.16,11.83 8.06,5.08"
        fill="#87ceeb"
      />
    </svg>
  );
}

function RailItem({ to, label, children }: { to: string; label: string; children: ReactNode }) {
  // end: "/" would otherwise match every route and pin Portfolio active.
  return (
    <NavLink to={to} end={to === '/'} className={({ isActive }) => 'rail__item' + (isActive ? ' is-on' : '')}>
      {children}
      <span>{label}</span>
    </NavLink>
  );
}

function AccountMenu() {
  const { me } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="menu" ref={ref}>
      <button
        className="avatar"
        aria-label="Account"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {me.user.initials}
      </button>
      {open && (
        <div className="menu__pop">
          <div className="menu__head">
            <b>{me.user.name}</b>
            <span>{me.user.roleLabel}</span>
          </div>
          <button
            onClick={() => {
              void signOut().then(() => navigate('/login'));
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function TopBar({ section }: { section: string }) {
  const { me } = useSession();
  return (
    <header className="top">
      <div className="top__crumb">
        <b>{me.company.name}</b>
        <span className="sep">/</span>
        <span>{section}</span>
      </div>
      <div className="top__actions">
        <AccountMenu />
      </div>
    </header>
  );
}

/** Rail + top bar around the routed content. Rendered inside RequireSession. */
function ShellFrame() {
  return (
    <div className="app">
      <nav className="rail" aria-label="Sections">
        <NavLink className="rail__brand" to="/properties" title="Tenpoint">
          <Decagon />
        </NavLink>

        <div className="rail__nav">
          <RailItem to="/" label="Portfolio">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="7.5" height="7.5" rx="1.4" />
              <rect x="13.5" y="4" width="7.5" height="7.5" rx="1.4" />
              <rect x="3" y="14.5" width="7.5" height="5.5" rx="1.4" />
              <rect x="13.5" y="14.5" width="7.5" height="5.5" rx="1.4" />
            </svg>
          </RailItem>
          <RailItem to="/properties" label="Properties">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21h18M5 21V8l7-4 7 4v13" />
              <path d="M9 12h2M13 12h2M9 16h6" />
            </svg>
          </RailItem>
          <RailItem to="/users" label="Users">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="8" r="3.2" />
              <path d="M2.5 20a6.5 6.5 0 0113 0" />
              <path d="M16.5 5.5a3 3 0 010 5.6M18 20a5.6 5.6 0 00-2.2-4.4" />
            </svg>
          </RailItem>
          <RailItem to="/settings" label="Settings">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.7 1.7 0 00.3 1.9l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.9-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1A1.7 1.7 0 008.9 19a1.7 1.7 0 00-1.9.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.9 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1A1.7 1.7 0 004.6 8.9a1.7 1.7 0 00-.3-1.9l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.9.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.9-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.9V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
            </svg>
          </RailItem>
        </div>

        <div className="rail__foot" />
      </nav>

      <div className="main" style={{ opacity: 1 }}>
        <TenantTopBar />
        <main className="page" id="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function TenantTopBar() {
  // Section name derives from the path's first segment, capitalised.
  const { pathname } = useLocation();
  const segment = pathname.split('/')[1] ?? '';
  const section = segment ? segment[0]!.toUpperCase() + segment.slice(1) : 'Portfolio';
  return <TopBar section={section} />;
}

export function TenantShell() {
  return (
    <RequireSession>
      <ShellFrame />
    </RequireSession>
  );
}
